import React, { useEffect, useMemo, useState } from 'react';
import { Header } from './header';
import { Footer } from './footer';
import { useNavigate, useParams } from 'react-router-dom';
import { listcategorycount, listtype } from '../services/homeser';
import {
    WeddingSEO,
    PubertyCardsSEO,
    HousewarmingSEO,
    EarpiecingSEO,
    BirthdaySEO,
    BusinessCardsSEO,
    SaveTheDateSEO,
} from './SEO';

/**
 * Reusable category-filtered product page.
 * categoryName must exactly match (case-insensitive) product_category.category_name from API.
 */
// Build a route slug from a category name (e.g. "Save the date" -> "savethedate")
const slugify = (name) => String(name || '').toLowerCase().replace(/\s+/g, '');

export const CategoryPage = ({ title, categoryName, SeoComponent }) => {
    const navigate = useNavigate();
    // For dynamic routes like /multicolor (no categoryName prop), derive the
    // category from the URL slug so only that category's products are shown.
    const { category: categorySlug } = useParams();
    const effectiveCategory = categoryName || categorySlug;
    const pageTitle = title || categorySlug || '';
    const [products, setProducts] = useState([]);
    const [tlist, setTlist] = useState([]);
    const [sortBy, setSortBy] = useState('');
    const [viewMode, setViewMode] = useState('grid');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(12);
    const [searchTerm, setSearchTerm] = useState('');
    const [priceRange, setPriceRange] = useState({ min: 2, max: 99999 });

    useEffect(() => {
        listcategorycount().then((res) => {
            if (res?.status) {
                // Filter products to only this category (by name, case-insensitive)
                const all = res.data.products || [];
                if (effectiveCategory) {
                    // Match by slug so URL params ("multicolor") line up with
                    // category names that may contain spaces ("Multi Color").
                    const wanted = slugify(effectiveCategory);
                    setProducts(
                        all.filter(p =>
                            slugify(p?.product_category?.category_name) === wanted
                        )
                    );
                } else {
                    setProducts(all); // No filter → show all (used by /cards)
                }
            }
        });

        listtype().then((res) => {
            if (res?.status) setTlist(res.data);
        });
    }, [effectiveCategory]);

    const filteredProducts = useMemo(() => {
        if (!products || products.length === 0) return [];
        return products.filter(item => {
            const currentPrice =
                item?.offer_price && Number(item.offer_price) > 0
                    ? Number(item.offer_price)
                    : Number(item.mrp_price);
            const matchesPrice = currentPrice >= priceRange.min && currentPrice <= priceRange.max;
            const matchesSearch = !searchTerm ||
                item?.product_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                item?.product_category?.category_name?.toLowerCase().includes(searchTerm.toLowerCase());
            const matchesType = !sortBy || sortBy === 'default' || String(item.type) === String(sortBy);
            return matchesPrice && matchesSearch && matchesType;
        });
    }, [products, priceRange, searchTerm, sortBy]);

    const totalItems = filteredProducts.length;
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredProducts.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(totalItems / itemsPerPage);

    return (
        <>
            {SeoComponent && <SeoComponent />}
            <Header />
            {/* Banner */}
            <section
                className="inner-section single-banner"
                style={{ background: "url(" + require("../assets/images/slider/single-banner.jpg") + ") no-repeat center" }}
            >
                <div className="container">
                    <h2>{pageTitle} Cards</h2>
                    <ol className="breadcrumb">
                        <li className="breadcrumb-item">
                            <a onClick={() => navigate('/')}>Home</a>
                        </li>
                        <li className="breadcrumb-item active" aria-current="page">{pageTitle} Cards</li>
                    </ol>
                </div>
            </section>

            {/* Products section */}
            <section className="inner-section shop-part">
                <div className="container">
                    <div className="row">
                        {/* ── Sidebar ─────────────────────────────── */}
                        <div className="col-lg-3">
                            <div className="shop-widget">
                                <h6 className="shop-widget-title">Filter by Price</h6>
                                <div className="shop-widget-group">
                                    <input
                                        type="number"
                                        placeholder="Min"
                                        defaultValue={2}
                                        onChange={(e) => setPriceRange(p => ({ ...p, min: Number(e.target.value) || 2 }))}
                                    />
                                    <input
                                        type="number"
                                        placeholder="Max"
                                        onChange={(e) => setPriceRange(p => ({ ...p, max: Number(e.target.value) || 99999 }))}
                                    />
                                </div>
                            </div>

                            <div className="shop-widget">
                                <h6 className="shop-widget-title">Search Cards</h6>
                                <form>
                                    <input
                                        className="shop-widget-search"
                                        type="text"
                                        placeholder="Search..."
                                        onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                                    />
                                </form>
                            </div>

                            <div className="shop-widget-promo d-none d-lg-block">
                                <a onClick={() => navigate('/weddingwebsite')}>
                                    <img src={require("../assets/images/website-tech-partner.jpg")} alt="wedding website" />
                                </a>
                            </div>
                        </div>

                        {/* ── Product Grid ─────────────────────────── */}
                        <div className="col-lg-9">
                            {/* Top filter bar */}
                            <div className="row">
                                <div className="col-lg-12">
                                    <div className="top-filter">
                                        <div className="filter-show">
                                            <label className="filter-label">Show :</label>
                                            <select className="form-select filter-select" onChange={(e) => setItemsPerPage(Number(e.target.value))}>
                                                <option value="12">12</option>
                                                <option value="24">24</option>
                                                <option value="36">36</option>
                                            </select>
                                        </div>
                                        <div className="filter-short">
                                            <label className="filter-label">Sort by :</label>
                                            <select className="form-select filter-select" onChange={(e) => setSortBy(e.target.value)} value={sortBy}>
                                                <option value="default">Default</option>
                                                {tlist.map(cat => (
                                                    <option key={cat.id} value={cat.id}>{cat?.type_name}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="filter-action">
                                            <a className={viewMode === 'grid' ? 'active' : ''} onClick={() => setViewMode('grid')}><i className="fas fa-th"></i></a>
                                            <a className={viewMode === 'large' ? 'active' : ''} onClick={() => setViewMode('large')}><i className="fas fa-th-large"></i></a>
                                            <a className={viewMode === 'list' ? 'active' : ''} onClick={() => setViewMode('list')}><i className="fas fa-list"></i></a>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Cards */}
                            {currentItems.length === 0 ? (
                                <div className="text-center py-5">
                                    <i className="fas fa-box-open" style={{ fontSize: '48px', color: '#ccc' }}></i>
                                    <p style={{ marginTop: '16px', color: '#888' }}>No {pageTitle} cards found.</p>
                                </div>
                            ) : (
                                <div className={`row ${viewMode === 'list' ? 'row-cols-1' : viewMode === 'large' ? 'row-cols-2 row-cols-md-3 row-cols-lg-2' : 'row-cols-2 row-cols-md-3 row-cols-lg-4'}`}>
                                    {currentItems.map((product) => (
                                        <div className="col" key={product.id}>
                                            <div className={`product-card ${viewMode === 'list' ? 'product-list' : ''}`}>
                                                <div className="product-media">
                                                    <div className="product-label">
                                                        <label className={`label-text ${product.tag}`}>{product.tag}</label>
                                                    </div>
                                                    {/* WhatsApp enquiry */}
                                                    <button
                                                        className="product-wish wish active"
                                                        onClick={() => {
                                                            const phone = "919865230900";
                                                            const message = encodeURIComponent(`Hi, I'd like to know more about: ${product.product_name}`);
                                                            window.open(`https://wa.me/${phone}?text=${message}`, "_blank");
                                                        }}
                                                    >
                                                        <i className="fab fa-whatsapp"></i>
                                                    </button>
                                                    <a className="product-image" onClick={() => navigate('/carddetails/' + product.id)}>
                                                        <img src={product.product_images[0]} alt={product.product_name} />
                                                    </a>
                                                </div>
                                                <div className="product-content">
                                                    <h6 className="product-name">
                                                        <a onClick={() => navigate('/carddetails/' + product.id)}>{product.product_name}</a>
                                                    </h6>
                                                    <h6 className="product-price">
                                                        {product?.offer_price != 0 ? (
                                                            <><del>₹{product?.mrp_price}</del><span>₹{product?.offer_price}<small>/piece</small></span></>
                                                        ) : (
                                                            <span>₹{product?.mrp_price}<small>/piece</small></span>
                                                        )}
                                                    </h6>
                                                    <button className="product-add" onClick={() => navigate('/carddetails/' + product.id)}>
                                                        <span>View Details</span>
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* Pagination */}
                            {totalPages > 1 && (
                                <div className="row">
                                    <div className="col-lg-12">
                                        <div className="bottom-paginate">
                                            <p className="page-info">Showing {indexOfFirstItem + 1}–{Math.min(indexOfLastItem, totalItems)} of {totalItems} Results</p>
                                            <ul className="pagination">
                                                <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                                    <a className="page-link" onClick={() => setCurrentPage(p => Math.max(p - 1, 1))}>
                                                        <i className="fas fa-long-arrow-alt-left"></i>
                                                    </a>
                                                </li>
                                                {[...Array(totalPages)].map((_, i) => (
                                                    <li key={i} className={`page-item ${currentPage === i + 1 ? 'active' : ''}`}>
                                                        <a className="page-link" onClick={() => setCurrentPage(i + 1)}>{i + 1}</a>
                                                    </li>
                                                ))}
                                                <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                                    <a className="page-link" onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))}>
                                                        <i className="fas fa-long-arrow-alt-right"></i>
                                                    </a>
                                                </li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Promo banner — shown at the bottom on mobile only */}
                        <div className="col-12 d-lg-none">
                            <div className="shop-widget-promo">
                                <a onClick={() => navigate('/weddingwebsite')}>
                                    <img src={require("../assets/images/website-tech-partner.jpg")} alt="wedding website" />
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <Footer />
        </>
    );
};

/* ── Named exports for each route ─────────────────────────── */
export const WeddingPage    = () => <CategoryPage title="Wedding"       categoryName="Wedding"       SeoComponent={WeddingSEO} />;
export const Puberty        = () => <CategoryPage title="Puberty"       categoryName="Puberty"       SeoComponent={PubertyCardsSEO} />;
export const Housewarming   = () => <CategoryPage title="Housewarming"  categoryName="Housewarming"  SeoComponent={HousewarmingSEO} />;
export const Earpiercing    = () => <CategoryPage title="Earpiercing"   categoryName="Earpiercing"   SeoComponent={EarpiecingSEO} />;
export const Birthday       = () => <CategoryPage title="Birthday"      categoryName="Birthday"      SeoComponent={BirthdaySEO} />;
export const BusinessCards  = () => <CategoryPage title="Business"      categoryName="Business"      SeoComponent={BusinessCardsSEO} />;
export const SaveTheDate    = () => <CategoryPage title="Save The Date" categoryName="Save the date" SeoComponent={SaveTheDateSEO} />;
